import { clearDealerSelection } from 'utils/auth/dealerSelection';
import { isNewEntrance, shouldReloadRestoredPage } from 'utils/auth/dealerEntrance';
import { user } from 'utils/auth/user';

declare global {
  var __ex3dDealerEntranceApplied: boolean | undefined;
}

let dealerIdWhenHidden: string | undefined;

function getNavigationType(): string | undefined {
  const entries = globalThis.performance?.getEntriesByType?.('navigation') ?? [];
  const [navigation] = entries as PerformanceNavigationTiming[];
  return navigation?.type;
}

function handlePageHide(): void {
  dealerIdWhenHidden = user.selectedDealerId || undefined;
}

function handlePageShow(event: Event): void {
  if (!(event as PageTransitionEvent).persisted) {
    return;
  }

  const currentDealerId = user.selectedDealerId || undefined;
  if (shouldReloadRestoredPage(dealerIdWhenHidden, currentDealerId)) {
    window.location.reload();
  }
}

/**
 * Standalone-only bootstrap (AVS-4405): clears the stored dealer choice on a new entrance and arms
 * the bfcache-restore reload guard. Runs once per document, before the router, the loaders, the gate
 * or the sign-in callback. Call it on the line before `createBrowserRouter` in
 * `src/router/routes.tsx`, the only place provably ahead of every loader.
 *
 * Guarded by a flag on `globalThis` rather than module scope, so a second call (e.g. a dev
 * hot-module re-evaluation of `routes.tsx`) does nothing even if the module itself re-evaluates.
 */
export function applyDealerEntrance(): void {
  if (globalThis.__ex3dDealerEntranceApplied) {
    return;
  }
  globalThis.__ex3dDealerEntranceApplied = true;

  const navigationType = getNavigationType();
  const referrer = document.referrer;
  const origin = window.location.origin;

  if (isNewEntrance({ navigationType, referrer, origin })) {
    clearDealerSelection();
  }

  window.addEventListener('pagehide', handlePageHide);
  window.addEventListener('pageshow', handlePageShow);
}

/** Test-only: lets a suite re-run `applyDealerEntrance()` as if a fresh document had loaded. */
export function resetDealerEntranceBootstrapForTests(): void {
  globalThis.__ex3dDealerEntranceApplied = undefined;
  dealerIdWhenHidden = undefined;
  window.removeEventListener('pagehide', handlePageHide);
  window.removeEventListener('pageshow', handlePageShow);
}
